import { useFindMany } from "@gadgetinc/react";
import { ArrowLeftIcon, ArrowRightIcon } from "@heroicons/react/16/solid";
import React, { useCallback, useContext, useEffect, useState } from "react";
import { api } from "../../api";
import { OverflowFrame, PageHeading } from "../../externalComponents";
import LoadingSpinner from "../../externalComponents/LoadingSpinner";
import { Badge } from "../../externalComponents/tailwind/badge";
import { Button } from "../../externalComponents/tailwind/button";
import EmptyState from "../../externalComponents/tailwind/empty-state";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../externalComponents/tailwind/table";
import { CustomerContext } from "../../providers";
import { useIsMobile } from "../../externalComponents/useIsMobile";

const BackInStockAlertsPage = () => {
  const { user } = useContext(CustomerContext);
  const isMobile = useIsMobile();

  const NUM_ON_PAGE = 10;

  const [cursor, setCursor] = useState({ first: NUM_ON_PAGE });
  const [pageNumber, setPageNumber] = useState(1);

  const [criteria, setCriteria] = useState({
    ...cursor,
    filter: [{ customerId: { equals: user?.customerId || "" } }],
  });

  useEffect(() => {
    if (user?.customerId) {
      setCriteria({
        ...cursor,
        filter: [{ customerId: { equals: user.customerId } }],
      });
    }
  }, [cursor, user?.customerId]);

  const [{ data, fetching }] = useFindMany(api.backInStockAlert, {
    ...criteria,
    select: {
      id: true,
      status: true,
      createdAt: true,
      contact: {
        id: true,
        firstName: true,
        lastName: true,
        userPhoneNumber: true,
      },
      campaign: {
        id: true,
        name: true,
      },
      shopifyProductVariant: {
        id: true,
        title: true,
        product: {
          id: true,
          title: true,
          handle: true,
        },
      },
    },
    sort: { createdAt: "Descending" },
  });

  const getNextPage = useCallback(() => {
    if (data?.hasNextPage && data?.endCursor) {
      setCursor({ first: NUM_ON_PAGE, after: data.endCursor });
      setPageNumber(pageNumber + 1);
    }
  }, [data, pageNumber]);

  const getPreviousPage = useCallback(() => {
    if (data?.hasPreviousPage && data?.startCursor) {
      setCursor({ last: NUM_ON_PAGE, before: data.startCursor });
      setPageNumber(pageNumber - 1);
    }
  }, [data, pageNumber]);

  const getStatusBadge = (status) => {
    switch (status) {
      case "subscription":
        return <Badge color="blue">Subscribed</Badge>;
      case "sent":
        return <Badge color="green">Sent</Badge>;
      default:
        return <Badge>{status}</Badge>;
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return new Intl.DateTimeFormat("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    }).format(date);
  };

  return (
    <>
      <PageHeading title="Back in Stock Alerts" />
      {fetching && <LoadingSpinner />}
      <OverflowFrame>
        {!fetching && (
          <>
            {data?.length > 0 ? (
              <Table>
                <TableHead>
                  <TableRow>
                    <TableHeader>Product</TableHeader>
                    {!isMobile && <TableHeader>Variant</TableHeader>}
                    <TableHeader>Contact</TableHeader>
                    {!isMobile && <TableHeader>Campaign</TableHeader>}
                    <TableHeader>Status</TableHeader>
                    <TableHeader>Created</TableHeader>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.map((alert) => {
                    const product = alert.shopifyProductVariant?.product;
                    const variant = alert.shopifyProductVariant;
                    const contact = alert.contact;
                    const contactName = contact
                      ? `${contact.firstName || ""} ${contact.lastName || ""}`.trim() ||
                        "Unknown"
                      : "No contact";

                    return (
                      <TableRow key={alert.id}>
                        <TableCell>
                          <div className="font-medium">
                            {product?.title || "Unknown Product"}
                          </div>
                          {isMobile && variant?.title && (
                            <div className="text-sm text-gray-500">
                              {variant.title}
                            </div>
                          )}
                        </TableCell>
                        {!isMobile && (
                          <TableCell>{variant?.title || "-"}</TableCell>
                        )}
                        <TableCell>
                          <div className="font-medium">{contactName}</div>
                          {contact?.userPhoneNumber && (
                            <div className="text-sm text-gray-500">
                              {contact.userPhoneNumber}
                            </div>
                          )}
                        </TableCell>
                        {!isMobile && (
                          <TableCell>{alert.campaign?.name || "-"}</TableCell>
                        )}
                        <TableCell>{getStatusBadge(alert.status)}</TableCell>
                        <TableCell>{formatDate(alert.createdAt)}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            ) : (
              <EmptyState
                heading="No back in stock alerts"
                img="https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png"
              />
            )}
          </>
        )}
        <div className="flex justify-start my-4">
          <Button
            color="secondaryButton"
            onClick={getPreviousPage}
            disabled={!data?.hasPreviousPage}
          >
            <ArrowLeftIcon />
            Previous
          </Button>
          <Badge className="my-1 mx-2 min-w-10 flex items-center justify-center">
            {pageNumber}
          </Badge>
          <Button
            color="secondaryButton"
            onClick={getNextPage}
            disabled={!data?.hasNextPage}
          >
            Next
            <ArrowRightIcon />
          </Button>
        </div>
      </OverflowFrame>
    </>
  );
};

export default BackInStockAlertsPage;
